import { Flex, Spinner, Text } from "@chakra-ui/react";
import { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CloudOfflineIcon } from "./CloudOfflineIcon";
import { CloudCheckmarkIcon } from "./CloudCheckmarkIcon";
import { Tooltip } from "@/components/ui/tooltip";

type SaveStatusProps = {
    isSaving: boolean;
    isSavingError: boolean;
    isNotSaved?: boolean;
    lastSavedRef: React.RefObject<number | null>;
};

const SavedStatuses = ({ isSaving, isSavingError, isNotSaved, lastSavedRef }: SaveStatusProps) => {
    const { t } = useTranslation("invoices");
    const [lastSavedMsg, setLastSavedMsg] = useState<string>();

    const getLastSaved = useCallback(
        (isError = false) => {
            if (!isError && lastSavedRef.current) {
                const SECONDS_IN_MINUTE = 60;
                const MILLISECONDS_TO_SECONDS = 1000;
                const currentTime = Date.now();
                const lastSavedTime = lastSavedRef.current;
                const differenceInMilliseconds = currentTime - lastSavedTime;
                const differenceInSeconds = Math.floor(differenceInMilliseconds / MILLISECONDS_TO_SECONDS);
                let timeAgoMessage = t("savedStatuses.lastSaved");

                if (differenceInSeconds < 1) {
                    timeAgoMessage += t("savedStatuses.lastSavedJustNow");
                    setLastSavedMsg(timeAgoMessage);
                }
                if (differenceInSeconds < SECONDS_IN_MINUTE) {
                    // If less than 60 seconds, display in seconds
                    timeAgoMessage += t("savedStatuses.lastSavedSeconds", { seconds: differenceInSeconds });
                } else {
                    // If 60 seconds or more, convert to minutes and display
                    const differenceInMinutes = Math.floor(differenceInSeconds / SECONDS_IN_MINUTE);
                    timeAgoMessage += t("savedStatuses.lastSavedMinutes", { minutes: differenceInMinutes });
                }
                setLastSavedMsg(timeAgoMessage);
            }

            if (isError) {
                setLastSavedMsg(t("savedStatuses.errorDescription") + "claimsinvoicing@gard.no");
            }
        },
        [lastSavedRef, t]
    );

    useEffect(() => {
        getLastSaved();
    }, [getLastSaved]);

    const handleErrorMouseOver = useCallback(() => {
        getLastSaved(true);
    }, [getLastSaved]);

    const handleSavedMouseOver = useCallback(() => {
        getLastSaved();
    }, [getLastSaved]);

    if (isSavingError) {
        return (
            <Tooltip
                content={lastSavedMsg}
                contentProps={{ bg: "anchor.red.600" }}
                showArrow
                positioning={{ placement: "top" }}
            >
                <Flex alignItems="center" gap="0.5rem" onMouseOver={handleErrorMouseOver}>
                    <CloudOfflineIcon color="anchor.gray.900" />
                    <Text color="anchor.red.800" data-testid="errorSavingText">
                        {t("savedStatuses.errorSaving")}
                    </Text>
                </Flex>
            </Tooltip>
        );
    }

    if (isSaving) {
        return (
            <Flex alignItems="center" gap="0.5rem">
                <Spinner color="anchor.gray.900" />
                <Text color="anchor.gray.900" data-testid="savingText">
                    {t("savedStatuses.saving")}
                </Text>
            </Flex>
        );
    }
    if (isNotSaved) {
        return (
            <Flex alignItems="center" gap="0.5rem">
                <CloudOfflineIcon color="anchor.gray.900" />
                <Text color="anchor.gray.900" data-testid="notSavedText">
                    {t("savedStatuses.notSaved")}
                </Text>
            </Flex>
        );
    }

    return (
        <Tooltip
            content={lastSavedMsg}
            contentProps={{ bg: "anchor.green.600" }}
            showArrow
            positioning={{ placement: "top" }}
        >
            <Flex alignItems="center" gap="0.5rem" onMouseOver={handleSavedMouseOver}>
                <CloudCheckmarkIcon color="anchor.gray.900" />
                <Text color="anchor.gray.900" data-testid="savedText">
                    {t("savedStatuses.saved")}
                </Text>
            </Flex>
        </Tooltip>
    );
};

export default SavedStatuses;
